import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../Components/CartContext";
import "./Cart.css";
import Footer from "../Components/Footer";
import Navbar from "./Components/Navbar";
import { checkDelivery, fetchSettings } from "../data/catalog";
import { trackBeginCheckout } from '../lib/analytics';

function Cart() {
  const { cart, removeFromCart, updateQuantity, getCartTotal } = useCart();
  const [deliveryOption, setDeliveryOption] = useState("local");
  const [fulfilment, setFulfilment] = useState({
    localDeliveryEnabled: true,
    storePickupEnabled: true,
    storePickup: { address: "", hours: "", instructions: "" },
    timeSlots: [
      { value: "12-2", label: "12:00 PM - 2:00 PM", startHour: 12, enabled: true },
      { value: "2-4", label: "2:00 PM - 4:00 PM", startHour: 14, enabled: true },
      { value: "4-6", label: "4:00 PM - 6:00 PM", startHour: 16, enabled: true },
      { value: "6-8", label: "6:00 PM - 8:00 PM", startHour: 18, enabled: true },
    ],
  });
  const [pinCode, setPinCode] = useState("");
  const [pinCodeValid, setPinCodeValid] = useState(false);
  const [pinCodeError, setPinCodeError] = useState("");
  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [deliveryArea, setDeliveryArea] = useState("");
  const [isCheckingPincode, setIsCheckingPincode] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState("");
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    console.log("Cart component mounted with cart:", cart);
  }, [cart]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load admin fulfilment settings; default the selection to an enabled option.
  useEffect(() => {
    let active = true;
    (async () => {
      const settings = await fetchSettings();
      if (!active || !settings || !settings.fulfilment) return;
      const f = settings.fulfilment;
      setFulfilment(f);
      setDeliveryOption((prev) => {
        if (prev === "local" && !f.localDeliveryEnabled) return f.storePickupEnabled ? "pickup" : "local";
        if (prev === "pickup" && !f.storePickupEnabled) return f.localDeliveryEnabled ? "local" : "pickup";
        return prev;
      });
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    setPinCode("");
    setPinCodeValid(false);
    setPinCodeError("");
    setDeliveryCharge(0);
    setDeliveryArea("");
    setSelectedDate("");
    setSelectedTimeSlot("");
    setAvailableTimeSlots([]);
  }, [deliveryOption]);

  useEffect(() => {
    if (selectedDate && (deliveryOption === "pickup" || pinCodeValid)) {
      updateAvailableTimeSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, deliveryOption, pinCodeValid, fulfilment]);

  // Local YYYY-MM-DD (matches the date input value).
  const localDateStr = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const updateAvailableTimeSlots = () => {
    // Only slots the admin has marked available.
    const enabled = (fulfilment.timeSlots || []).filter((s) => s.enabled !== false);
    const now = new Date();
    const cutoff = now.getTime() + 60 * 60 * 1000; // bookable only 1 hour+ from now
    const todayStr = localDateStr(now);

    let available = enabled;
    if (selectedDate === todayStr) {
      // For same-day bookings, hide slots starting within the next hour.
      available = enabled.filter((s) => {
        const start = new Date(`${selectedDate}T${String(s.startHour).padStart(2, "0")}:00:00`);
        return start.getTime() >= cutoff;
      });
    }

    setAvailableTimeSlots(available);
    setSelectedTimeSlot("");
  };

  const handleQuantityChange = (item, newQuantity) => {
    console.log('handleQuantityChange called with:', item, newQuantity);
    
    if (newQuantity === 0) {
      removeFromCart(item.id, item.variant);
    } else {
      updateQuantity(item.id, newQuantity, item.variant);
    }
  };

  const handleRemoveItem = (item) => {
    console.log('handleRemoveItem called with:', item);
    removeFromCart(item.id, item.variant);
  };

  const handleCheckPinCode = async () => {
    if (!pinCode.trim()) {
      setPinCodeError("Please enter a pincode");
      return;
    }

    setIsCheckingPincode(true);
    setPinCodeError("");
    setPinCodeValid(false);
    setDeliveryCharge(0);
    setDeliveryArea("");

    const data = await checkDelivery(pinCode.trim());

    if (data.available) {
      setPinCodeValid(true);
      setDeliveryCharge(data.deliveryCharge);
      setDeliveryArea(data.area);
      setPinCodeError("");
    } else {
      setPinCodeValid(false);
      setDeliveryCharge(0);
      setDeliveryArea("");
      setPinCodeError(data.message || "Delivery not available at this pincode");
    }
    setIsCheckingPincode(false);
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleTimeSlotSelect = (slot) => {
    setSelectedTimeSlot(slot);
  };

  const validateCheckout = () => {
    if (cart.items.length === 0) return false;
    // Block the chosen method if the admin has disabled it.
    if (deliveryOption === "local" && !fulfilment.localDeliveryEnabled) return false;
    if (deliveryOption === "pickup" && !fulfilment.storePickupEnabled) return false;
    if (deliveryOption === "local" && !pinCodeValid) return false;
    if (!selectedDate) return false;
    if (!selectedTimeSlot) return false;

    // The chosen slot must be admin-enabled and (if today) at least 1 hour away.
    const slot = (fulfilment.timeSlots || []).find((s) => s.value === selectedTimeSlot);
    if (!slot || slot.enabled === false) return false;

    const now = new Date();
    if (selectedDate === localDateStr(now)) {
      const start = new Date(`${selectedDate}T${String(slot.startHour).padStart(2, "0")}:00:00`);
      if (start.getTime() < now.getTime() + 60 * 60 * 1000) return false;
    }

    return true;
  };

  const handleCheckout = () => {
    if (validateCheckout()) {
      console.log("Proceeding to checkout");
      trackBeginCheckout(cart.items || [], getCartTotal());
      navigate("/checkout", {
        state: {
          deliveryOption,
          pinCode: deliveryOption === "local" ? pinCode : null,
          deliveryCharge: deliveryOption === "local" ? deliveryCharge : 0,
          deliveryArea: deliveryOption === "local" ? deliveryArea : null,
          selectedDate,
          selectedTimeSlot,
        },
      });
    } else {
      alert("Please complete all required fields");
    }
  };

  const handleContinueShopping = () => {
    navigate("/");
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const getTotalWithDelivery = () => {
    const subtotal = getCartTotal();
    const delivery = deliveryOption === "local" && pinCodeValid ? deliveryCharge : 0;
    return subtotal + delivery;
  };

  const TruckIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="1" y="3" width="15" height="13"></rect>
      <polygon points="16,8 20,8 23,11 23,16 16,16 16,8"></polygon>
      <circle cx="5.5" cy="18.5" r="2.5"></circle>
      <circle cx="18.5" cy="18.5" r="2.5"></circle>
    </svg>
  );

  const StoreIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7"></path>
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
      <path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4"></path>
      <path d="M2 7h20"></path>
      <path d="M22 7v3a2 2 0 0 1-2 2v0a2.18 2.18 0 0 1-2.18-2.18 2.18 2.18 0 0 0-2.18-2.18 2.18 2.18 0 0 0-2.18 2.18 2.18 2.18 0 0 1-2.18 2.18 2.18 2.18 0 0 1-2.18-2.18 2.18 2.18 0 0 0-2.18-2.18 2.18 2.18 0 0 0-2.18 2.18A2.18 2.18 0 0 1 4 12v0a2 2 0 0 1-2-2V7"></path>
    </svg>
  );

  const ChevronDownIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="6,9 12,15 18,9"></polyline>
    </svg>
  );

  console.log("Cart rendering with items:", cart.items);

  // ⭐ FIXED: Render Navbar and Footer even when cart is empty
  return (
    <>
      <Navbar />
      <div className="cartContainer">
        {cart.items.length === 0 ? (
          // Empty Cart View
          <div className="emptyCart">
            <h2>Your Cart is Empty</h2>
            <p>Add some delicious items to get started!</p>
            <button
              className="continueShoppingBtn"
              onClick={handleContinueShopping}
            >
              Continue Shopping
            </button>
            <button className="trackOrderLink" onClick={() => navigate('/myorders')}>
              Track an existing order
            </button>
          </div>
        ) : (
          // Cart with Items View
          <>
            <h2 className="cartTitle">YOUR CART</h2>
            <div className="cartContent">
              <div className="cartLeft">
                <div className="cartItems">
                  {cart.items.map((item) => (
                    <div key={`${item.id}_${item.variant}`} className="cartItem">
                      <div className="itemImage">
                        <img src={item.image} alt={item.name} />
                      </div>

                      <div className="itemDetails">
                        <h3 className="itemName">{item.name}</h3>
                        <p className="itemVariant">{item.variant}</p>
                        <p className="itemPrice">£{typeof item.price === 'number' ? item.price.toFixed(2) : item.price}</p>

                        <div className="quantityControls">
                          <button
                            className="quantityBtnCart"
                            onClick={() => handleQuantityChange(item, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                          >
                            −
                          </button>
                          <span className="quantityCart">{item.quantity}</span>
                          <button
                            className="quantityBtnCart"
                            onClick={() => handleQuantityChange(item, item.quantity + 1)}
                          >
                            +
                          </button>
                        </div>

                        <button
                          className="removeBtn"
                          onClick={() => handleRemoveItem(item)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="cartRight">
                <div className="orderSummary">
                  <div className="subtotal">
                    <span>Subtotal: £{typeof getCartTotal() === 'number' ? getCartTotal().toFixed(2) : getCartTotal()}</span>
                  </div>
                  <div className="deliveryOptions">
                    <div className="deliveryOption">
                      <button
                        className={`deliveryBtn ${
                          deliveryOption === "local" ? "active" : ""
                        } ${!fulfilment.localDeliveryEnabled ? "disabled" : ""}`}
                        onClick={() => fulfilment.localDeliveryEnabled && setDeliveryOption("local")}
                        disabled={!fulfilment.localDeliveryEnabled}
                        title={!fulfilment.localDeliveryEnabled ? "Currently unavailable" : ""}
                      >
                        <div className="deliveryIcon">
                          <TruckIcon />
                        </div>
                        <span>Local Delivery</span>
                        {!fulfilment.localDeliveryEnabled && (
                          <span className="deliveryUnavailable">Currently unavailable</span>
                        )}
                      </button>

                      <button
                        className={`deliveryBtn ${
                          deliveryOption === "pickup" ? "active" : ""
                        } ${!fulfilment.storePickupEnabled ? "disabled" : ""}`}
                        onClick={() => fulfilment.storePickupEnabled && setDeliveryOption("pickup")}
                        disabled={!fulfilment.storePickupEnabled}
                        title={!fulfilment.storePickupEnabled ? "Currently unavailable" : ""}
                      >
                        <div className="deliveryIcon">
                          <StoreIcon />
                        </div>
                        <span>Store Pickup</span>
                        {!fulfilment.storePickupEnabled && (
                          <span className="deliveryUnavailable">Currently unavailable</span>
                        )}
                      </button>
                    </div>
                  </div>

                  {deliveryOption === "pickup" && fulfilment.storePickupEnabled &&
                    (fulfilment.storePickup.address || fulfilment.storePickup.hours || fulfilment.storePickup.instructions) && (
                    <div className="pickupInfo">
                      {fulfilment.storePickup.address && (
                        <p><strong>Pickup at:</strong> {fulfilment.storePickup.address}</p>
                      )}
                      {fulfilment.storePickup.hours && (
                        <p><strong>Hours:</strong> {fulfilment.storePickup.hours}</p>
                      )}
                      {fulfilment.storePickup.instructions && (
                        <p className="pickupInstructions">{fulfilment.storePickup.instructions}</p>
                      )}
                    </div>
                  )}

                  {deliveryOption === "local" && (
                    <div className="pinCodeSection">
                      <label htmlFor="pincode">Enter your pin code</label>
                      <div className="pinCodeInput">
                        <input
                          type="text"
                          id="pincode"
                          value={pinCode}
                          onChange={(e) => setPinCode(e.target.value)}
                          placeholder="Enter pin code"
                          className={pinCodeError ? "error" : ""}
                          disabled={isCheckingPincode}
                        />
                        <button 
                          className="checkBtn" 
                          onClick={handleCheckPinCode}
                          disabled={isCheckingPincode}
                        >
                          {isCheckingPincode ? "Checking..." : "Check"}
                        </button>
                      </div>
                      {pinCodeError && (
                        <div className="errorText">{pinCodeError}</div>
                      )}
                      {pinCodeValid && (
                        <div className="successText">
                          Delivery available 
                        </div>
                      )}
                    </div>
                  )}

                  {(deliveryOption === "pickup" ||
                    (deliveryOption === "local" && pinCodeValid)) && (
                    <div className="dateTimeSection">
                      <div className="dateSelection">
                        <label htmlFor="deliveryDate">Select Date</label>
                        <input
                          type="date"
                          id="deliveryDate"
                          value={selectedDate}
                          onChange={handleDateChange}
                          min={getMinDate()}
                          className="dateInput"
                        />
                      </div>

                      {selectedDate && (
                        <div className="timeSlotSelection">
                          <label>Select Time Slot</label>
                          {availableTimeSlots.length > 0 ? (
                            <div className="timeSlots">
                              {availableTimeSlots.map((slot) => (
                                <button
                                  key={slot.value}
                                  className={`timeSlotBtn ${
                                    selectedTimeSlot === slot.value
                                      ? "selected"
                                      : ""
                                  }`}
                                  onClick={() => handleTimeSlotSelect(slot.value)}
                                >
                                  {slot.label}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="noSlotsText">
                              No time slots available for today. Please select
                              another date.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    className={`checkoutBtn ${
                      !validateCheckout() ? "disabled" : ""
                    }`}
                    onClick={handleCheckout}
                    disabled={!validateCheckout()}
                  >
                    CHECKOUT
                  </button>

                  <button className="trackOrderLink" onClick={() => navigate('/myorders')}>
                    Track an existing order
                  </button>

                  <button
                    className="continueShoppingBtn secondary"
                    onClick={handleContinueShopping}
                  >
                    CONTINUE SHOPPING
                  </button>
                </div>

                
              </div>
            </div>
          </>
        )}
      </div>
      <Footer />
    </>
  );
}

export default Cart;
